/**
 * Text metrics for the SVG diagram.
 *
 * SVG `<text>` has no box model, so a node cannot be sized by its content the
 * way a `<div>` can — the layout pass has to know how wide a title will be
 * BEFORE anything is drawn. We measure against a 2D canvas with the same font
 * the diagram renders with, which is accurate to within a pixel or so, and
 * cache by `font|text` because the same titles are measured on every relayout.
 */

let ctx: CanvasRenderingContext2D | null | undefined
const cache = new Map<string, number>()

function context(): CanvasRenderingContext2D | null {
  if (ctx === undefined) {
    ctx = document.createElement('canvas').getContext('2d')
  }
  return ctx
}

/** Width of `text` in px at `font` (a CSS `font` shorthand). */
export function textWidth(text: string, font: string): number {
  const key = `${font}|${text}`
  const hit = cache.get(key)
  if (hit !== undefined) return hit
  const c = context()
  // jsdom and other canvas-less hosts: fall back to an average-advance
  // estimate. Slightly wrong boxes beat throwing during a server render.
  const w = c ? ((c.font = font), c.measureText(text).width) : text.length * 6.6
  cache.set(key, w)
  return w
}

export interface WrappedText {
  lines: string[]
  /** Width of the widest line. */
  width: number
}

/**
 * Greedy word wrap to at most `maxLines`, ellipsising the last line when the
 * title genuinely does not fit. Long single words (a URL, a German compound)
 * are broken by character rather than allowed to blow out the column.
 */
export function wrapText(
  text: string,
  font: string,
  maxWidth: number,
  maxLines: number,
): WrappedText {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (!clean) return { lines: [''], width: 0 }
  if (textWidth(clean, font) <= maxWidth) {
    return { lines: [clean], width: textWidth(clean, font) }
  }

  const words = clean.split(' ')
  const lines: string[] = []
  let line = ''

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const candidate = line ? `${line} ${word}` : word
    if (textWidth(candidate, font) <= maxWidth) {
      line = candidate
      continue
    }
    if (line) {
      lines.push(line)
      line = ''
      i-- // retry this word on the fresh line
    } else {
      // A single word wider than the column: hard-break it.
      let cut = word.length - 1
      while (cut > 1 && textWidth(word.slice(0, cut), font) > maxWidth) cut--
      lines.push(word.slice(0, cut))
      words[i] = word.slice(cut)
      i--
    }
    if (lines.length === maxLines) break
  }
  if (line && lines.length < maxLines) lines.push(line)

  if (lines.length === maxLines) {
    // Anything we did not reach belongs to the truncated tail.
    const consumed = lines.join(' ').replace(/\s+/g, ' ')
    if (consumed.length < clean.length) {
      let last = lines[maxLines - 1]
      while (last.length > 1 && textWidth(`${last}…`, font) > maxWidth) {
        last = last.slice(0, -1)
      }
      lines[maxLines - 1] = `${last.trimEnd()}…`
    }
  }

  return {
    lines,
    width: Math.max(...lines.map((l) => textWidth(l, font))),
  }
}
